import cn from '@integrations/classnames'
import type { HTMLAttributes, ReactNode } from '@integrations/react'
import { PAGE_FILL } from '../../Layout/PageShell'
import styles from './EmptyState.module.scss'

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The glyph inside the tile (pass `currentColor` icons: the tile sets the colour). */
  icon: ReactNode
  title: ReactNode
  message?: ReactNode
  /** The next step, usually a primary button. */
  action?: ReactNode
  /** Put the empty state in a full-height list card of its own (a `fill` page with no list to show). */
  card?: boolean
}

/**
 * The empty state of a list card: an icon tile with a yellow dot, a bold title, a short message and the next step,
 * centered in the space the card has (it fills a `page-fill-card` and centers; in a short card it starts at the top).
 */
function EmptyState({ icon, title, message, action, card, className, ...props }: Props) {
  const content = (
    <div className={cn(styles['empty-state'], 'empty-state', className)} {...props}>
      <span className={cn(styles['empty-state__icon'], 'empty-state__icon')} aria-hidden='true'>
        {icon}
        <span className={cn(styles['empty-state__dot'], 'empty-state__dot')} />
      </span>
      <h3 className={cn(styles['empty-state__title'], 'empty-state__title')}>{title}</h3>
      {message && <p className={cn(styles['empty-state__message'], 'empty-state__message')}>{message}</p>}
      {action}
    </div>
  )

  return card ? <div className={cn('table-surface', PAGE_FILL.card)}>{content}</div> : content
}

export default EmptyState
